// src/components/ResponsiveGrid.js

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./ResponsiveGrid.css";

import React, { useEffect, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";

import CardFactory from "../factories/CardFactory";
import { usePointerEvents } from "../hooks/usePointerEvents";

const ResponsiveGridLayout = WidthProvider(Responsive);

const ResponsiveGrid = ({
  cards,
  topics,
  layouts,
  updateLayouts,
  setSelectedCard,
}) => {
  const containerRef = useRef(null);
  const pointerLocation = useRef({ x: 0, y: 0 });
  const clickedItem = useRef(null); // Store the clicked item temporarily

  const cardRefs = useRef({}); // Store refs for each card

  const [containerWidth, setContainerWidth] = useState(1200); // Default width
  const breakpoints = { lg: 1800, md: 1200, sm: 768, xs: 480, xxs: 0 };
  const cols = { lg: 18, md: 12, sm: 8, xs: 5, xxs: 4 }; // Columns for each breakpoint
  const adjustment = { lg: -12, md: -11, sm: -11, xs: -10, xxs: -12 }; // Adjustment so 1x1 is square

  const [mounted, setMounted] = useState(false);

  // compute the initial breakpoint based on screen width
  let initialBreakpoint;
  for (const [breakpoint, width] of Object.entries(breakpoints)) {
    if (containerWidth >= width) {
      initialBreakpoint = breakpoint;
      break;
    }
  }
  const [currentBreakpoint, setCurrentBreakpoint] = useState(initialBreakpoint);

  // Dynamically calculate rowHeight
  const calculateRowHeight = () => {
    // const currentLayout = layouts[currentBreakpoint] || layouts.lg;
    const colCount = cols[currentBreakpoint] || cols.lg;
    const rh =
      Math.floor(containerWidth / colCount) + adjustment[currentBreakpoint]; // Ensure rowHeight matches colWidth
    return rh;
  };

  const rowHeight = calculateRowHeight();

  // Update container width dynamically
  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateContainerWidth(); // Initial calculation
    window.addEventListener("resize", updateContainerWidth);
    return () => window.removeEventListener("resize", updateContainerWidth); // Cleanup
  }, []);

  usePointerEvents({
    pointerLocation,
    clickedItem,
    cardRefs,
    setSelectedCard,
  });

  // Generate layouts for each breakpoint based on cards
  const generateLayouts = () => {
    const layouts = {};
    Object.keys(cols).forEach((breakpoint) => {
      const colCount = cols[breakpoint];
      layouts[breakpoint] = cards.map((item, index) => {
        const w = 2; // Set widget width to 2 for all breakpoints
        const h = 2; // Set widget height to 2 for all breakpoints
        const x = (index * w) % colCount;
        const y = Math.floor((index * w) / colCount) * h;
        return {
          i: item.id.toString(),
          x,
          y,
          w,
          h,
          minW: 1,
          minH: 1,
        };
      });
    });
    return layouts;
  };

  const [layoutsState, setLayoutsState] = useState(
    layouts || generateLayouts()
  );

  useEffect(() => {
    if (layouts) {
      setLayoutsState(layouts);
    } else {
      setLayoutsState(generateLayouts());
    }
  }, [layouts, cards, topics]);

  useEffect(() => {
    // If the number of cards has changed, update the layouts
    const itemIds = cards.map((item) => item.id.toString());
    const layoutIds = new Set();
    Object.values(layoutsState).forEach((breakpointLayouts) => {
      breakpointLayouts.forEach((layout) => layoutIds.add(layout.i));
    });

    const missingIds = itemIds.filter((id) => !layoutIds.has(id));
    const extraIds = Array.from(layoutIds).filter(
      (id) => !itemIds.includes(id)
    );

    if (missingIds.length > 0 || extraIds.length > 0) {
      // Update layouts to add missing cards and remove extra cards
      const updatedLayouts = { ...layoutsState };
      Object.keys(updatedLayouts).forEach((breakpoint) => {
        let breakpointLayouts = updatedLayouts[breakpoint];

        // Remove layouts with extraIds
        breakpointLayouts = breakpointLayouts.filter(
          (layout) => !extraIds.includes(layout.i)
        );

        // Add missing layouts
        missingIds.forEach((id) => {
          const w = 2;
          const h = 2;
          const x = 0; // You can choose how to position new cards
          const y = Infinity; // Place at the bottom
          breakpointLayouts.push({
            i: id,
            x,
            y,
            w,
            h,
            minW: 1,
            minH: 1,
          });
        });

        updatedLayouts[breakpoint] = breakpointLayouts;
      });

      setLayoutsState(updatedLayouts);
    }
  }, [cards, layoutsState]);

  const onLayoutChange = (_, allLayouts) => {
    updateLayouts("grid", allLayouts);
  };

  useEffect(() => {
    setTimeout(() => {
      setMounted(true);
    }, 200);
  }, []);

  const getTopicForCard = (card) => {
    let topic = topics.find((topic) => topic.id === card.topics[0].id);
    return topic;
  };

  return (
    /* use style to hide scrollbar */
    <div
      className={`grid-container ${mounted ? "animated" : ""}`}
      ref={containerRef}
      style={{
        overflow: "auto",
        scrollbarWidth: "none", // Firefox
        msOverflowStyle: "none", // IE and Edge
        "&::WebkitScrollbar": {
          display: "none", // Hide scrollbar for Chrome, Safari, and Opera
        },
      }}
    >
      <ResponsiveGridLayout
        className="layout"
        layouts={layoutsState}
        breakpoints={breakpoints}
        cols={cols}
        rowHeight={rowHeight} // Dynamically calculated row height
        onLayoutChange={onLayoutChange}
        onBreakpointChange={(newBreakpoint) =>
          setCurrentBreakpoint(newBreakpoint)
        } // Track current breakpoint
        resizeHandles={["se"]} // Specify which resize handles to display
        compactType={null}
        preventCollision={true}
      >
        {cards.map((card) => {
          if (card.type != "display") {
            return null; // Skip non-display cards
          }

          const currentLayout =
            layoutsState[currentBreakpoint] || layoutsState.lg;

          const layoutItem = currentLayout.find(
            (layout) => layout.i === card.id.toString()
          );

          // Fallback values in case layoutItem is undefined
          const w = layoutItem ? layoutItem.w : 2;
          const h = layoutItem ? layoutItem.h : 2;

          if (!cardRefs.current[card.id]) {
            cardRefs.current[card.id] = React.createRef();
          }
          const cardRef = cardRefs.current[card.id];

          const theTopic = getTopicForCard(card);
          if (!theTopic) {
            return null;
          }

          return (
            <div key={card.id} className="grid-item">
              <div className="content w-full h-full">
                <div
                  className="cursor-pointer w-full h-full" // Ensure cursor-pointer div fills the grid cell
                  onPointerDown={(e) => {
                    pointerLocation.current = { x: e.clientX, y: e.clientY };
                    clickedItem.current = card; // Set the currently clicked item
                  }}
                >
                  <CardFactory
                    card={card}
                    topic={theTopic}
                    w={w}
                    h={h}
                    ref={cardRef}
                    breakpoint={currentBreakpoint}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
};

export default ResponsiveGrid;
